import {DateTime} from 'luxon';
import {connect} from 'react-redux';

import {computeJobProgress} from '../../../../../../../../../src/helpers/jobHelpers';
import {CrmSyncSourcesEnum} from '../../../../../../../../../src/models/api/crmSyncModel';
import {
  makeGetCompanyHasCrmSyncFeatureViaSource,
  makeGetCrmSyncsViaSource,
} from '../../../../../../../../../src/store/data/crmSync/crmSyncsSelectors';
import {getAllRunningSyncJobs} from '../../../../../../../../../src/store/data/jobs/jobsSelectors';
import {getIsUserGlobalAdmin} from '../../../../../../../../../src/store/data/teammates/teammatesSelectors';
import {
  createSelector,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../../srcCommon/helpers/reselectHelpers';
import {Handler} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {CrmSyncsInfoBoxes} from './crmSyncsInfoBoxes';

/*
 * Constants.
 */

const hoursTillNextSyncFromPrevious = 24;

/*
 * Props.
 */

export interface CrmSyncsInfoBoxesContainerPublicProps {
  source: CrmSyncSourcesEnum;
  showNextSync: boolean;
  canSyncNow: boolean;
  onSync: Handler;
}

type CrmSyncsInfoBoxesContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
export type CrmSyncsInfoBoxesContainerProps = CrmSyncsInfoBoxesContainerPublicProps &
  CrmSyncsInfoBoxesContainerStateProps;

/*
 * Selectors.
 */

function makeGetLastSyncedAtViaSource() {
  return createSelector([makeGetCrmSyncsViaSource()], (crmSyncs) => {
    if (crmSyncs.length === 0) {
      return undefined;
    }
    const crmSync = crmSyncs.reduce((a, b) => (a.lastSyncedAt < b.lastSyncedAt ? b : a));
    return crmSync.lastSyncedAt === 0 ? undefined : crmSync.lastSyncedAt;
  });
}

function makeGetNextSyncViaSource() {
  return createSelector([makeGetLastSyncedAtViaSource()], (lastSyncedAt) => {
    if (!lastSyncedAt) {
      return undefined;
    }

    return DateTime.fromMillis(lastSyncedAt).plus({hours: hoursTillNextSyncFromPrevious}).valueOf();
  });
}

function makeGetActiveSyncJobProgressPercentageViaSource() {
  return createSelector([makeGetCrmSyncsViaSource(), getAllRunningSyncJobs], (crmSyncs, jobs) => {
    if (crmSyncs.length === 0) {
      return undefined;
    }
    const syncIds = crmSyncs.map((sync) => sync.id);
    const syncJobs = jobs.filter((job) => syncIds.includes(job.sync_id));

    if (syncJobs.length === 0) {
      return undefined;
    }
    const oldestRunningJob = syncJobs.reduce((a, b) => (a.createdAt < b.createdAt ? a : b));

    const jobProgress = computeJobProgress(oldestRunningJob);
    if (jobProgress === 1) {
      // don't return progress when it's completed
      return undefined;
    }
    // we want the percentage and computeJobProgress returns a decimal
    return Math.floor(100 * jobProgress);
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CrmSyncsInfoBoxesContainerPublicProps>()(() => ({
  lastSyncedAt: makeGetLastSyncedAtViaSource(),
  nextSyncAt: makeGetNextSyncViaSource(),
  hasCrmSyncFeature: makeGetCompanyHasCrmSyncFeatureViaSource(),
  jobProgressPercentage: makeGetActiveSyncJobProgressPercentageViaSource(),
  isAdmin: getIsUserGlobalAdmin,
}));

export const CrmSyncsInfoBoxesContainer = connect(mapStateToProps)(CrmSyncsInfoBoxes);
